import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { 
  Calendar, 
  Cloud, 
  Star, 
  Package,
  Lightbulb,
  MapPin,
  Info,
  ChevronDown,
  BarChart3
} from 'lucide-react'
import type { StructuredItinerary, FlightResult, Hotel, FlightAnalysis, FlightSearchParamsForAnalysis } from "@/types/travel"
import { ItinerarySegmentCard } from "./ItinerarySegmentCard"
import { FlightAnalysisTab } from "./FlightAnalysisTab"
import { cn } from "@/lib/utils"

interface StructuredItineraryViewProps {
  itinerary: StructuredItinerary
  availableFlights?: FlightResult[]
  availableHotels?: Hotel[]
  onSelectItem?: (itemId: string, type: 'flight' | 'hotel') => void
  // VOY-18: Flight Analysis props
  searchParams?: FlightSearchParamsForAnalysis
  flightAnalysis?: FlightAnalysis | null
  isLoadingAnalysis?: boolean
}

// Format date to mm/dd/yyyy (with timezone fix)
function formatDate(dateStr: string): string {
  try {
    // Parse date without timezone shift
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    const localDate = new Date(year, month - 1, day);
    
    const mm = (localDate.getMonth() + 1).toString().padStart(2, '0');
    const dd = localDate.getDate().toString().padStart(2, '0');
    const yyyy = localDate.getFullYear();
    
    return `${mm}/${dd}/${yyyy}`;
  } catch {
    return dateStr; // Return original if parsing fails
  }
}

export function StructuredItineraryView({ 
  itinerary,
  availableFlights,
  availableHotels,
  onSelectItem,
  // VOY-18: Flight Analysis props
  searchParams,
  flightAnalysis,
  isLoadingAnalysis = false
}: StructuredItineraryViewProps) {
  // Set default tab to "analysis" if we have analysis data or are loading it
  const hasAnalysis = flightAnalysis || isLoadingAnalysis
  const [activeTab, setActiveTab] = useState(hasAnalysis ? "analysis" : "timeline")
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set([1])) // Solo Day 1 expandido por defecto
  
  // Update active tab when analysis becomes available
  useEffect(() => {
    if (flightAnalysis && activeTab !== "analysis") {
      // Only switch if we're on timeline and analysis just loaded
      // Don't force switch if user manually selected another tab
    }
  }, [flightAnalysis, activeTab])
  
  const toggleDay = (dayNumber: number) => {
    const newExpanded = new Set(expandedDays)
    if (newExpanded.has(dayNumber)) {
      newExpanded.delete(dayNumber)
    } else {
      newExpanded.add(dayNumber)
    }
    setExpandedDays(newExpanded)
  }
  
  return (
    <div className="space-y-4">
      {/* Summary Section */}
      {itinerary.summary && (
        <Card className="border-gray-200/50 dark:border-zinc-700/50 bg-blue-50/50 dark:bg-zinc-800/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-gray-900 dark:text-white">
              <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              Trip Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-gray-800 dark:text-gray-200 font-medium">{itinerary.summary.greeting}</p>
            
            {itinerary.summary.weather && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Cloud className="h-4 w-4" />
                <span>{itinerary.summary.weather}</span>
              </div>
            )}
            
            {itinerary.summary.highlights && itinerary.summary.highlights.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Star className="h-4 w-4 text-yellow-500" />
                  <span>Highlights</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {itinerary.summary.highlights.map((highlight, idx) => (
                    <Badge key={idx} variant="secondary" className="rounded-full">
                      {highlight}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={cn(
          "grid w-full bg-gray-100 dark:bg-zinc-800/50",
          hasAnalysis ? "grid-cols-3" : "grid-cols-2"
        )}>
          {/* VOY-18: Flight Analysis Tab - First Position */}
          {hasAnalysis && (
            <TabsTrigger value="analysis" className="flex items-center gap-2 text-gray-600 data-[state=active]:text-gray-900 data-[state=active]:bg-white dark:text-gray-400 dark:data-[state=active]:bg-zinc-700/80 dark:data-[state=active]:text-white">
              <BarChart3 className="h-4 w-4" />
              Analysis
            </TabsTrigger>
          )}
          <TabsTrigger value="timeline" className="flex items-center gap-2 text-gray-600 data-[state=active]:text-gray-900 data-[state=active]:bg-white dark:text-gray-400 dark:data-[state=active]:bg-zinc-700/80 dark:data-[state=active]:text-white">
            <Calendar className="h-4 w-4" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="essentials" className="flex items-center gap-2 text-gray-600 data-[state=active]:text-gray-900 data-[state=active]:bg-white dark:text-gray-400 dark:data-[state=active]:bg-zinc-700/80 dark:data-[state=active]:text-white">
            <Package className="h-4 w-4" />
            Essentials
          </TabsTrigger>
        </TabsList>
        
        {/* VOY-18: Flight Analysis Tab Content */}
        {hasAnalysis && (
          <TabsContent value="analysis" className="mt-4">
            <FlightAnalysisTab 
              analysis={flightAnalysis}
              searchParams={searchParams}
              isLoading={isLoadingAnalysis}
            />
          </TabsContent>
        )}
        
        {/* Timeline Tab */}
        <TabsContent value="timeline" className="mt-4">
          <div className="space-y-6 pr-4">
              {itinerary.timeline.map((day, dayIdx) => {
                const isExpanded = expandedDays.has(day.day)
                
                return (
                <div 
                  key={dayIdx} 
                  className="space-y-3"
                >
                  {/* Day Header - Clickeable para expandir/colapsar */}
                  <div 
                    className="bg-gray-50 dark:bg-zinc-900/80 backdrop-blur-sm pb-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700/50 transition-colors rounded-lg p-2 -mx-2"
                    onClick={() => toggleDay(day.day)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="font-semibold rounded-full dark:bg-zinc-700 dark:text-white dark:border-zinc-600">
                          Day {day.day}
                        </Badge>
                        {day.date && (
                          <span className="text-sm text-muted-foreground dark:text-gray-400">
                            {formatDate(day.date)}
                          </span>
                        )}
                        <ChevronDown className={cn(
                          "h-4 w-4 transition-transform text-muted-foreground dark:text-gray-400",
                          isExpanded && "rotate-180"
                        )} />
                      </div>
                      {day.title && (
                        <span className="text-sm font-medium text-primary dark:text-blue-400">
                          {day.title}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Day Segments - Solo visible si está expandido */}
                  {isExpanded && (() => {
                    // Group segments by time period
                    const groupedSegments = day.segments.reduce((acc, segment) => {
                      const timeKey = segment.time;
                      if (!acc[timeKey]) {
                        acc[timeKey] = [];
                      }
                      acc[timeKey].push(segment);
                      return acc;
                    }, {} as Record<string, typeof day.segments>);

                    // Order time periods
                    const timeOrder: (typeof day.segments[0]['time'])[] = [
                      'arrival', 'morning', 'afternoon', 'evening', 'night', 'departure'
                    ];

                    return (
                      <div className="space-y-4 relative animate-in fade-in-0 slide-in-from-top-2 duration-300">
                        {/* Timeline Line - Hidden for cleaner glassmorphism design */}
                        {/* <div className="absolute left-[106px] top-0 bottom-0 w-px bg-gray-300/30 dark:bg-zinc-700/50" /> */}
                        
                        {timeOrder
                          .filter(time => groupedSegments[time]?.length > 0)
                          .map((timePeriod) => {
                            const segments = groupedSegments[timePeriod];
                            
                            return (
                              <div key={timePeriod} className="space-y-2">
                                {/* Time Period Header - Always show for consistency */}
                                <div className="flex items-center gap-2 mb-2 ml-[120px]">
                                  <Badge 
                                    variant="secondary"
                                    className={cn(
                                      "min-w-[80px] justify-center rounded-full text-xs",
                                      timePeriod === 'arrival' && 'bg-blue-50 text-blue-900 border border-blue-200 dark:bg-blue-950 dark:text-blue-100',
                                      timePeriod === 'morning' && 'bg-sky-50 text-sky-900 border border-sky-200 dark:bg-sky-950 dark:text-sky-100',
                                      timePeriod === 'afternoon' && 'bg-teal-50 text-teal-900 border border-teal-200 dark:bg-teal-950 dark:text-teal-100',
                                      timePeriod === 'evening' && 'bg-indigo-50 text-indigo-900 border border-indigo-200 dark:bg-indigo-950 dark:text-indigo-100',
                                      timePeriod === 'night' && 'bg-violet-50 text-violet-900 border border-violet-200 dark:bg-violet-950 dark:text-violet-100',
                                      timePeriod === 'departure' && 'bg-slate-50 text-slate-900 border border-slate-200 dark:bg-slate-950 dark:text-white'
                                    )}
                                  >
                                    {timePeriod === 'arrival' && 'Arrival'}
                                    {timePeriod === 'morning' && 'Morning'}
                                    {timePeriod === 'afternoon' && 'Afternoon'}
                                    {timePeriod === 'evening' && 'Evening'}
                                    {timePeriod === 'night' && 'Night'}
                                    {timePeriod === 'departure' && 'Departure'}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    {segments.length} {segments.length === 1 ? 'activity' : 'activities'}
                                  </span>
                                </div>
                                
                                {/* Segments in this time period */}
                                {segments.map((segment, segIdx) => (
                                  <div key={segIdx} className="relative">
                                    <ItinerarySegmentCard
                                      segment={segment}
                                      availableFlights={availableFlights}
                                      availableHotels={availableHotels}
                                      onSelectItem={onSelectItem}
                                      hideTimeBadge={true} // Always hide time badge when using grouped header
                                    />
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                      </div>
                    );
                  })()}
                  
                  {dayIdx < itinerary.timeline.length - 1 && (
                    <Separator className="mt-4" />
                  )}
                </div>
              )})}
          </div>
        </TabsContent>
        
        {/* Essentials Tab */}
        <TabsContent value="essentials" className="mt-4">
          <div className="space-y-4">
            {itinerary.essentials.transportation && itinerary.essentials.transportation.length > 0 && (
              <Card className="bg-white dark:bg-zinc-800/50 backdrop-blur-sm border-gray-200 dark:border-zinc-700/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-gray-900 dark:text-white">
                    <MapPin className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                    Transportation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {itinerary.essentials.transportation.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-zinc-300">
                        <Info className="h-3 w-3 text-gray-400 dark:text-zinc-500 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            
            {itinerary.essentials.packing && itinerary.essentials.packing.length > 0 && (
              <Card className="bg-white dark:bg-zinc-800/50 backdrop-blur-sm border-gray-200 dark:border-zinc-700/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-gray-900 dark:text-white">
                    <Package className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                    Packing List
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {itinerary.essentials.packing.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-zinc-300">
                        <Info className="h-3 w-3 text-gray-400 dark:text-zinc-500 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            
            {itinerary.essentials.localTips && itinerary.essentials.localTips.length > 0 && (
              <Card className="bg-white dark:bg-zinc-800/50 backdrop-blur-sm border-gray-200 dark:border-zinc-700/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-gray-900 dark:text-white">
                    <Lightbulb className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                    Local Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {itinerary.essentials.localTips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-zinc-300">
                        <Info className="h-3 w-3 text-gray-400 dark:text-zinc-500 mt-0.5" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}